import React from 'react';
import { Box, BoxProps, Text } from 'rebass';
import { ErrorInfo } from './NotifyProvider';
import { ApolloError } from '@apollo/client';

export type ErrorDetailsProps = {
  errorDetails: ErrorInfo;
} & BoxProps;

const ErrorDetails = ({
  errorDetails,
  ...boxProps
}: ErrorDetailsProps): JSX.Element => {
  if (typeof errorDetails === 'string') {
    return <StringError {...(boxProps as any)} error={errorDetails} />;
  }
  if (errorDetails instanceof ApolloError) {
    return <ApolloErrorFormat {...(boxProps as any)} error={errorDetails} />;
  }
  if (errorDetails instanceof Error) {
    return <ErrorFormat {...(boxProps as any)} error={errorDetails} />;
  }
  return <></>;
};

const StringError = ({
  error,
  ...boxProps
}: { error: string } & BoxProps): JSX.Element => {
  return (
    <Box
      marginBottom={1}
      fontSize={1}
      fontFamily="monospace"
      width="full"
      {...(boxProps as any)}
    >
      {error}
    </Box>
  );
};

const Stack = ({ stack }: { stack: string }): JSX.Element => {
  return (
    <>
      {stack.split(/\n/g).map((line, index) => (
        <Box key={index}>{line}</Box>
      ))}
    </>
  );
};

const NetworkErrors = ({ errors }: { errors: string[] }): JSX.Element => {
  return (
    <>
      {errors.map((error, index) => (
        <Box key={index}>{error}</Box>
      ))}
    </>
  );
};

const ErrorFormat = ({
  error,
  ...boxProps
}: { error: Error } & BoxProps): JSX.Element => {
  if (!error.stack && error.message) {
    return <StringError error={error.message} />;
  }

  return (
    <Box
      marginBottom={1}
      fontSize={1}
      overflow="auto"
      maxHeight={300}
      fontFamily="monospace"
      width="full"
      sx={{ whiteSpace: 'pre-wrap' }}
      {...(boxProps as any)}
    >
      {error.stack && <Stack stack={error.stack} />}
    </Box>
  );
};

const ApolloErrorFormat = ({
  error,
  ...boxProps
}: { error: ApolloError } & BoxProps): JSX.Element => {
  if (!error) {
    return <></>;
  }

  let networkErrors = [];
  if ((error.networkError as any)?.result?.errors) {
    networkErrors = (error.networkError as any).result.errors.map(
      ({ message }: { message: string }) => message
    );
  }

  const filtered = (
    Object.keys(JSON.parse(JSON.stringify(error))) as (keyof ApolloError)[]
  )
    .filter((key) => {
      const value = error[key];
      return (
        key !== 'message' &&
        key !== 'stack' &&
        !!value &&
        (!Array.isArray(value) || value.length > 0)
      );
    })
    .reduce(
      (result, key) => {
        const value = error[key];
        if (key === 'networkError' && value?.result?.errors) {
          return result;
        }
        result[key] = value;
        return result;
      },
      {} as Record<string, any>
    );

  const errorKeys = Object.keys(filtered);

  return (
    <Box
      marginBottom={1}
      fontSize={1}
      overflow="auto"
      fontFamily="monospace"
      width="full"
      sx={{ whiteSpace: 'pre-wrap' }}
      {...(boxProps as any)}
    >
      {error.message && <StringError error={error.message} />}
      {networkErrors.length > 0 && <NetworkErrors errors={networkErrors} />}
      {/*{errorKeys.length > 0 && (*/}
      {/*  <Stack stack={JSON.stringify(filtered, null, 2)} />*/}
      {/*)}*/}
      {/*{errorKeys.length > 0 &&*/}
      {/*  errorKeys.map((errorKey) => {*/}
      {/*    return (*/}
      {/*      <Text key={errorKey}>*/}
      {/*        {createErrorDetail(filtered[errorKey], errorKey)}*/}
      {/*      </Text>*/}
      {/*    )*/}
      {/*  })}*/}
    </Box>
  );
};

const createErrorDetail = (errorValue: any, errorKey: string) => {
  let resolvedError: Record<string, string>;

  switch (errorKey) {
    case 'graphQLErrors':
      resolvedError = convertErrorArray(errorValue);
      return (
        <Text marginBottom={1} fontSize={1} fontFamily="monospace">
          {errorKey}:<br />
          {resolvedError.message}
          <br />
          {resolvedError.path}
          <br />
          {resolvedError.extraContext}
        </Text>
      );

    case 'clientErrors':
      resolvedError = convertErrorArray(errorValue);
      return (
        <Text marginBottom={1} fontSize={1} fontFamily="monospace">
          {errorKey}:<br />
          {resolvedError.message}
          <br />
          {resolvedError.path}
          <br />
          {resolvedError.extraContext}
        </Text>
      );

    default:
      return (
        <Text marginBottom={1} fontSize={1} fontFamily="monospace">
          {errorKey}:<br />
          {JSON.stringify(errorValue)}
        </Text>
      );
  }
};

// Helper functions
const convertErrorArray = (errorArray: []) => {
  let message = '';
  let locations = '';
  let path = '';
  let extraContext = '';
  for (let i = 0; i < errorArray.length; i++) {
    if (errorArray[i]['message']) {
      message = `Message: ${errorArray[i]['message']}`;
    }
    if (errorArray[i]['locations']) {
      locations = `Locations: ${convertError(
        errorArray[i]['locations'],
        'locations'
      )}`;
    }
    if (errorArray[i]['path']) {
      path = `Path: ${convertError(errorArray[i]['path'], 'path')}`;
    } else extraContext = JSON.stringify(errorArray[i]);
  }
  return { message, path, locations, extraContext };
};

const convertError = (error: [], key: string) => {
  const errors: string[] = [];
  switch (key) {
    case 'locations':
      for (let i = 0; i < error.length; i++) {
        errors.push(`column: ${error[i]['column']}, line: ${error[i]['line']}`);
      }
      return errors.join(', ');
    case 'path':
      for (let i = 0; i < error.length; i++) {
        errors.push(`${error[i]}`);
      }
      return errors.join(', ');
    default:
      return JSON.stringify(error);
  }
};

export default ErrorDetails;
